import { graphql } from 'react-relay';
import { useEffect, useState } from 'react';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type {
	useContentOwnershipContributorsContentOwnershipContributorsQuery as useContentOwnershipContributorsContentOwnershipContributorsQuery$data,
	useContentOwnershipContributorsContentOwnershipContributorsQueryVariables as useContentOwnershipContributorsContentOwnershipContributorsQuery$variables,
} from './__types__/useContentOwnershipContributorsContentOwnershipContributorsQuery.ts';
import { DEFAULT_CONTRIBUTORS_FETCH_LIMIT } from './contentOwnershipConstants.ts';
import { parseContributors } from './content-ownership-helpers.ts';
import type { UserType } from './ownership-models.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
export const useContentOwnershipContributors = ({
	contentId,
}: {
	contentId: string;
	spaceKey: string; // TODO Remove this unused prop
}) => {
	const [arrangedContributors, setArrangedContributors] = useState<UserType[]>([]);
	const { data, error } = useQueryInterop<
		useContentOwnershipContributorsContentOwnershipContributorsQuery$data,
		useContentOwnershipContributorsContentOwnershipContributorsQuery$variables
	>(
		graphql`
			query useContentOwnershipContributorsContentOwnershipContributorsQuery(
				$contentId: ID!
				$limit: Int = 4
			) {
				content(id: $contentId) {
					nodes {
						id
						history {
							ownedBy {
								displayName
								type
								... on KnownUser {
									accountId
								}
								profilePicture {
									path
								}
							}
						}
						version {
							by {
								displayName
								type
								... on KnownUser {
									accountId
								}
								profilePicture {
									path
								}
							}
						}
					}
				}
				contentContributors(id: $contentId, limit: $limit) {
					nodes {
						displayName
						type
						... on KnownUser {
							accountId
						}
						profilePicture {
							path
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
				limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
			},
		},
	);

	if (error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		} else {
			// TODO: add analytics or experience tracking for errors
		}
	}

	useEffect(() => {
		if (data) {
			const contributors = parseContributors(data);
			setArrangedContributors(contributors);
		}
	}, [data]);

	return arrangedContributors;
};
